import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import {
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  EmojiEventsOutlined as EmojiEventsOutlinedIcon,
  InsightsOutlined as InsightsOutlinedIcon,
  MenuBookOutlined as MenuBookOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  SchoolOutlined as SchoolOutlinedIcon,
  TrendingUpOutlined as TrendingUpOutlinedIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../api/axiosConfig';
import PageContainer from '../../common/PageContainer';
import PageHeader from '../../common/PageHeader';
import EmptyState from '../../common/EmptyState';
import { STORAGE_KEYS } from '../../../constants';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toneForValue = (value) => {
  if (value == null) return 'default';
  if (value >= 80) return 'success';
  if (value >= 60) return 'info';
  return 'warning';
};

const progressColor = (value) => {
  if (value == null) return '#94a3b8';
  if (value >= 80) return '#16a34a';
  if (value >= 60) return '#2563eb';
  return '#f59e0b';
};

const StudentStatistics = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const schoolId = localStorage.getItem(STORAGE_KEYS.SCHOOL_ID);

  const [student, setStudent] = useState(null);
  const [overview, setOverview] = useState({});
  const [monthlyAcademicSummary, setMonthlyAcademicSummary] = useState([]);
  const [subjectPerformance, setSubjectPerformance] = useState([]);
  const [configuredCategories, setConfiguredCategories] = useState([]);
  const [savedResultsPendingPublish, setSavedResultsPendingPublish] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAllData = useCallback(async () => {
    if (!studentId) {
      setLoading(false);
      setErrorMessage('Student ID is missing.');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axiosInstance.get(`/api/student-statistics/${studentId}`, {
        headers: schoolId ? { schoolId } : {},
      });

      const payload = response?.data || {};
      setStudent(payload.student || null);
      setOverview(payload.overview || {});
      setMonthlyAcademicSummary(Array.isArray(payload.monthlyAcademicSummary) ? payload.monthlyAcademicSummary : []);
      setSubjectPerformance(Array.isArray(payload.subjectPerformance) ? payload.subjectPerformance : []);
      setConfiguredCategories(Array.isArray(payload.configuredCategories) ? payload.configuredCategories : []);
      setSavedResultsPendingPublish(Boolean(payload.savedResultsPendingPublish));
    } catch (error) {
      setStudent(null);
      setErrorMessage(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Unable to load student statistics right now.'
      );
    } finally {
      setLoading(false);
    }
  }, [schoolId, studentId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const topCards = useMemo(() => ([
    {
      label: 'Attendance',
      value: overview?.attendancePercentage != null ? `${overview.attendancePercentage}%` : 'N/A',
      helper: `${overview?.daysPresent || 0} present / ${overview?.daysAbsent || 0} absent`,
      icon: <CalendarMonthOutlinedIcon fontSize="small" />,
      color: '#2563eb',
    },
    {
      label: 'Academic Avg',
      value: overview?.academicAverage != null ? `${overview.academicAverage}%` : 'N/A',
      helper: 'Across recorded exams this year',
      icon: <TrendingUpOutlinedIcon fontSize="small" />,
      color: '#0f9d7a',
    },
    {
      label: 'Best Subject',
      value: overview?.bestSubject || 'N/A',
      helper: `Weak: ${overview?.weakSubject || 'N/A'}`,
      icon: <MenuBookOutlinedIcon fontSize="small" />,
      color: '#7c3aed',
    },
    {
      label: 'Class Rank',
      value: overview?.classRank ? `#${overview.classRank}` : 'N/A',
      helper: overview?.classStrength ? `Out of ${overview.classStrength}` : `${overview?.achievementCount || 0} achievements`,
      icon: <EmojiEventsOutlinedIcon fontSize="small" />,
      color: '#d97706',
    },
  ]), [overview]);

  const focusNotes = useMemo(() => {
    const notes = [];
    if ((overview?.attendancePercentage || 0) < 75) {
      notes.push('Attendance is below 75%. Parent follow-up may be needed.');
    }
    if ((overview?.academicAverage || 0) > 0 && (overview?.academicAverage || 0) < 60) {
      notes.push('Academic average is below 60%. Review weak subjects first.');
    }
    const latestAttentionMonth = monthlyAcademicSummary.find((row) => row.status === 'Action needed');
    if (latestAttentionMonth) {
      notes.push(`${latestAttentionMonth.monthLabel} needs attention based on attendance or exam performance.`);
    }
    if (!notes.length) {
      notes.push('This student is in a stable academic range this year.');
    }
    return notes;
  }, [monthlyAcademicSummary, overview]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!student) {
    return (
      <PageContainer>
        <EmptyState
          title="Student statistics unavailable"
          description={errorMessage || 'The requested student could not be loaded.'}
          action={(
            <Button variant="outlined" onClick={() => navigate('/students/list')}>
              Back to Students
            </Button>
          )}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Student Statistics"
        subtitle={`${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student'}
        actions={(
          <Button variant="contained" onClick={() => navigate(`/students/${studentId}/view-student`)}>
            View Student
          </Button>
        )}
      />

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 1.5,
          background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.info.light, 0.12)} 100%)`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Avatar
                src={student.imageUrl || ''}
                sx={{ width: 92, height: 92, borderRadius: 4, bgcolor: 'rgba(37, 99, 235, 0.10)', color: 'primary.main' }}
              >
                {!student.imageUrl ? <PersonOutlineRoundedIcon fontSize="large" /> : null}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {`${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Admission No: {student.admissionNumber || 'N/A'} | Class: {overview?.className || student?.classes?.className || 'N/A'}{overview?.sectionName || student?.section?.sectionName ? ` - ${overview?.sectionName || student?.section?.sectionName}` : ''}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
                  {configuredCategories.map((label) => (
                    <Chip key={label} size="small" label={label} variant="outlined" />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.7)' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <SchoolOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Academic Snapshot
                </Typography>
              </Stack>
              <Table size="small">
                <TableBody>
                  {[
                    ['Academic Year', student.academicYear || 'N/A'],
                    ['Best Subject', overview?.bestSubject || 'N/A'],
                    ['Weak Subject', overview?.weakSubject || 'N/A'],
                    ['Achievements', `${overview?.achievementCount || 0}`],
                  ].map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell sx={{ py: 0.8, px: 0, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>{label}</TableCell>
                      <TableCell sx={{ py: 0.8, px: 0, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 700 }}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {topCards.map((item) => (
          <Grid item xs={12} sm={6} lg={3} key={item.label}>
            <Card variant="outlined" sx={{ borderRadius: 1.5, height: '100%' }}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.2 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(item.color, 0.12),
                      color: item.color,
                    }}
                  >
                    {item.icon}
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.6 }}>{item.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                  {item.helper}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {savedResultsPendingPublish ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          Result rows are saved for this student, but the exam is not published yet. Student statistics only show published exam data.
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={8}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <InsightsOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Month-wise Academic Progress
              </Typography>
            </Stack>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Attendance</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Exam Avg</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Best Subject</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Weak Subject</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rank</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Achievements</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyAcademicSummary.length > 0 ? monthlyAcademicSummary.map((row) => (
                    <TableRow key={row.monthKey} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{row.monthLabel}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.attendancePercentage ? `${row.attendancePercentage}%` : 'N/A'}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={row.attendancePercentage || 0}
                          sx={{ mt: 0.7, height: 7, borderRadius: 6, '& .MuiLinearProgress-bar': { backgroundColor: progressColor(row.attendancePercentage) } }}
                        />
                      </TableCell>
                      <TableCell>{row.examAverage != null ? `${row.examAverage}%` : 'N/A'}</TableCell>
                      <TableCell>{row.bestSubject || 'N/A'}</TableCell>
                      <TableCell>{row.weakSubject || 'N/A'}</TableCell>
                      <TableCell>{row.classRank ? `#${row.classRank}${row.classStrength ? ` / ${row.classStrength}` : ''}` : 'N/A'}</TableCell>
                      <TableCell>{row.achievementCount || 0}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status || 'N/A'}
                          color={row.status === 'Strong' ? 'success' : row.status === 'Watch' ? 'warning' : row.status === 'Action needed' ? 'error' : 'default'}
                          variant={row.status === 'No data' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          {savedResultsPendingPublish
                            ? 'No published exam data is available yet for month-wise statistics.'
                            : 'No academic statistics are available yet.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <MenuBookOutlinedIcon color="secondary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Subject Performance
              </Typography>
            </Stack>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Average</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Latest</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Exam Count</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subjectPerformance.length > 0 ? subjectPerformance.map((row) => (
                    <TableRow key={row.subjectName} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{row.subjectName}</TableCell>
                      <TableCell>{row.averagePercentage != null ? `${row.averagePercentage}%` : 'N/A'}</TableCell>
                      <TableCell>{row.latestPercentage != null ? `${row.latestPercentage}%` : 'N/A'}</TableCell>
                      <TableCell>{row.examCount || 0}</TableCell>
                      <TableCell>{formatDate(row.latestDate)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status || 'N/A'}
                          color={row.status === 'Awaiting results' ? 'default' : toneForValue(row.averagePercentage)}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          {savedResultsPendingPublish
                            ? 'Saved marks exist, but subject performance will appear here only after the exam is published.'
                            : 'No published subject performance is available yet.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.2 }}>
                Admin Focus
              </Typography>
              <Stack spacing={1}>
                {focusNotes.map((note) => (
                  <Alert key={note} severity={note.includes('stable') ? 'success' : 'warning'} variant="outlined">
                    {note}
                  </Alert>
                ))}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.2 }}>
                Quick Student Table
              </Typography>
              <Table size="small">
                <TableBody>
                  {[
                    ['Roll No', student.rollNumber || 'N/A'],
                    ['Admission No', student.admissionNumber || 'N/A'],
                    ['Class', overview?.className || student?.classes?.className || 'N/A'],
                    ['Section', overview?.sectionName || student?.section?.sectionName || 'N/A'],
                    ['Parent', student.parentName || 'N/A'],
                    ['Phone', student.parentPhone || student.phone || 'N/A'],
                    ['Status', student.status || 'N/A'],
                  ].map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell sx={{ px: 0, py: 0.9, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>{label}</TableCell>
                      <TableCell sx={{ px: 0, py: 0.9, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 700 }}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default StudentStatistics;
